import gql from "graphql-tag";

export interface TranscriptLine {
  speaker: string;
  text: string;
}

export interface Vendor {
  name: string;
  slug: string;
  vendorId: string;
}

export interface Sector {
  name: string;
  slug: string;
  sectorId: string;
}

export interface Speaker {
  name: string | null;
  title: string | null;
}

export interface EventDetails {
  date: string;
  time: string;
  location: string;
  address: string;
  type: string;
  description: string;
  speakers: Speaker[];
  organization: string;
  teaserQuotes: string[];
  signupUri?: string;
}

export interface ProductInput {
  name: string;
  description: string;
  previewDescription: string;
  isActive: boolean;
  isAvailable: boolean;
  isFree: boolean;
  isForSale: boolean;
  publishDate: string;
  files: string[];
  vendors: Vendor[];
  sectors: Sector[];
}

export interface SharedInsightsProductInput
  extends Omit<ProductInput, "files"> {
  details: SharedInsightsProduct["details"];
  files: FileList | null;
  proprietaryDataset: string;
}

export interface InsightsProductInput extends ProductInput {
  eventDetails: EventDetails;
  transcriptLines: TranscriptLine[] | null;
  transcriptSpeakers: string[] | null;
  transcriptRaw: string | null;
}

export interface Product {
  id: string;
  name: string;
  dataId: string;
  isActive: boolean;
  isAvailable: boolean;
  isFree: boolean;
  isForSale: boolean;
  description: string;
  previewDescription: string;
  publishDate: string;
  slug: string[];
  files: string[];
  roles: string[];
  vendors: Vendor[];
  sectors: Sector[];
  priority: number;
  proprietaryDataset: string;
  createdAt: string;
  updatedAt: string;
  logo: string | null;
}

export interface InsightsProduct extends Product {
  eventDetails: EventDetails;
  transcriptLines: TranscriptLine[] | [];
  transcript: string | null;
  transcriptSpeakers: string[] | null;
  proprietaryDataset: "VENN";
}

export interface SharedInsightsProduct extends Product {
  details: {
    pageCount: string;
    eventCount: string;
    eventStartDate: string | null;
    eventEndDate: string | null;
    teaserQuotes: string[];
    tags: string[];
  };
  proprietaryDataset:
    | "VEC"
    | "VendorFocus"
    | "IndustryAnalysis"
    | "ObservatoryReport";
}

export const EventDetailsInfo = gql`
  fragment EventDetailsInfo on EventDetails {
    date
    time
    location
    address
    type
    description
    speakers {
      title
      name
    }
    signupUri
    organization
    vendorsMentioned
    sectorsMentioned
    teaserQuotes
  }
`;

export const AdminInsightsProductInfo = gql`
  fragment AdminInsightsProductInfo on AdminInsightsProduct {
    id
    name
    dataId
    isActive
    isAvailable
    isFree
    isForSale
    description
    previewDescription
    publishDate
    slug
    files
    roles
    transcript
    transcriptSpeakers
    vendors {
      name
      slug
      vendorId
    }
    sectors {
      name
      slug
      sectorId
    }
    priority
    proprietaryDataset
    createdAt
    logo
  }
`;

export const SharedInsightsDetailsInfo = gql`
  fragment SharedInsightsDetailsInfo on SharedInsightsDetails {
    pageCount
    eventCount
    eventStartDate
    eventEndDate
    teaserQuotes
    tags
  }
`;

export const AdminSharedInsightsProductInfo = gql`
  fragment AdminSharedInsightsProductInfo on AdminSharedInsightsProduct {
    id
    name
    dataId
    isActive
    isAvailable
    isFree
    isForSale
    description
    previewDescription
    publishDate
    slug
    files
    roles
    vendors {
      name
      slug
      vendorId
    }
    sectors {
      name
      slug
      sectorId
    }
    priority
    proprietaryDataset
    createdAt
    logo
  }
`;
